import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { OcSidebarPartnerLogo } from "oc/components";
import { getPrintStyles } from "./my-requests-dashboard-card-print.styles";
import { formatDateHu, formatPeriodHu, sortEntriesByRawDate } from "my-timesheet/utils";

interface DailyEntry {
  _id: string;
  name: string;
  description?: string;
  projectName?: string;
  clientName?: string;
  costName?: string;
  startTime: string;
  endTime: string;
  duration: string;
  rawDate: string;
  date: string;
  costBillable?: boolean;
}

interface MyTimesheetMyRequestsDashboardCardPrintProps {
  periodStart: string;
  employeeName: string;
  contractCode: string;
  hoursSummary: string;
  dailyEntries: DailyEntry[];
}

export const MyTimesheetMyRequestsDashboardCardPrint: React.FC<
  MyTimesheetMyRequestsDashboardCardPrintProps
> = ({
  periodStart,
  employeeName,
  contractCode,
  hoursSummary,
  dailyEntries,
}) => {
    const { t } = useTranslation();

    const sortedEntries = sortEntriesByRawDate(dailyEntries);

    return (
      <Box>
        <style>{getPrintStyles(employeeName, contractCode)}</style>

        <Box
          display="flex"
          flexDirection="column"
          sx={{
            "@media print": {
              padding: "0",
            },
          }}
        >
          <Box marginBottom="40px" textAlign="center" className="print-no-break">
            <Typography variant="h5" style={{ fontSize: "18px", fontWeight: 700 }}>
              {t("MY_TIMESHEET_TIMESHEET_REPORT")}
            </Typography>
            <Typography variant="h6" style={{ fontSize: "16px", fontWeight: 400, marginTop: "10px" }}>
              {formatPeriodHu(periodStart)}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" marginBottom="40px" className="print-no-break">
            <Box>
              <Box display="flex" flexDirection="row" marginBottom="8px">
                <Typography variant="body2" style={{ fontSize: "13px", minWidth: "140px" }}>
                  {t("OC_CALENDAR_DATE")}
                </Typography>
                <Typography variant="body2" style={{ fontSize: "13px", fontWeight: 600 }}>
                  {formatDateHu(new Date().toISOString())}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="row" marginBottom="8px">
                <Typography variant="body2" style={{ fontSize: "13px", minWidth: "140px" }}>
                  {t("OC_EMPLOYEE_NAME")}
                </Typography>
                <Typography variant="body2" style={{ fontSize: "13px", fontWeight: 600 }}>
                  {employeeName}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="row" marginBottom="8px">
                <Typography variant="body2" style={{ fontSize: "13px", minWidth: "140px" }}>
                  {t("MY_CAFETERIA_STATEMENT_CONTRACT_CODE")}
                </Typography>
                <Typography variant="body2" style={{ fontSize: "13px", fontWeight: 600 }}>
                  {contractCode}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "none",
                "@media print": {
                  display: "block",
                },
              }}
            >
              <OcSidebarPartnerLogo />
            </Box>
          </Box>
          <Box marginBottom="40px" className="print-no-break">
            <Box display="flex" flexDirection="row" alignItems="baseline">
              <Typography variant="body1" style={{ fontSize: "14px", marginRight: "16px" }}>
                {t("MY_TIMESHEET_TOTAL_DURATION")}
              </Typography>
              <Typography variant="h6" style={{ fontSize: "20px", fontWeight: 700 }}>
                {hoursSummary} {t("OC_HOUR").toLowerCase()}
              </Typography>
            </Box>
          </Box>
          <Table size="small" sx={{ marginBottom: "60px" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "transparent" }}>
                <TableCell style={{ fontSize: "14px", fontWeight: 700, padding: "8px 4px", borderBottom: "2px solid #000" }}>
                  {t("MY_TIMESHEET_ACTIVITY")}
                </TableCell>
                <TableCell style={{ fontSize: "14px", fontWeight: 700, padding: "8px 4px", borderBottom: "2px solid #000" }}>
                  {t("MY_TIMESHEET_PROJECT")}
                </TableCell>
                <TableCell style={{ fontSize: "14px", fontWeight: 700, padding: "8px 4px", borderBottom: "2px solid #000" }}>
                  {t("MY_TIMESHEET_CLIENT")}
                </TableCell>
                <TableCell align="right" style={{ fontSize: "14px", fontWeight: 700, padding: "8px 4px", borderBottom: "2px solid #000" }}>
                  {t("MY_TIMESHEET_DURATION")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEntries.map((entry) => (
                <TableRow key={entry._id} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                  <TableCell style={{ fontSize: "12px", padding: "8px 4px", border: "none" }}>
                    {entry.name}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", padding: "8px 4px", border: "none" }}>
                    {entry.projectName || "-"}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", padding: "8px 4px", border: "none" }}>
                    {entry.clientName || "-"}
                  </TableCell>
                  <TableCell align="right" style={{ fontSize: "12px", padding: "8px 4px", border: "none", fontWeight: 600 }}>
                    {entry.duration}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" flexDirection="column" paddingTop="40px" className="print-no-break">
            <Typography variant="body2" style={{ fontSize: "12px", marginBottom: "40px" }}>
              {t("OC_DATE")}:
            </Typography>
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <Box borderBottom="1px solid #000" width="200px" marginBottom="8px" />
                <Typography variant="body2" style={{ fontSize: "12px", textAlign: "center" }}>
                  {t("MY_TIMESHEET_PERFORMER_SIGNATURE")}
                </Typography>
              </Box>
              <Box>
                <Box borderBottom="1px solid #000" width="200px" marginBottom="8px" />
                <Typography variant="body2" style={{ fontSize: "12px", textAlign: "center" }}>
                  {t("MY_TIMESHEET_CERTIFIER_SIGNATURE")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

export default MyTimesheetMyRequestsDashboardCardPrint;
